/**
 * FeedbackButton.js — floating "Feedback" pill, bottom-right.
 *
 * Mounted once inside AppLayout (company portal only) so it appears on every
 * company-user-facing page. The admin and supplier portals use separate
 * layout wrappers, so this button is structurally invisible there — no
 * route-level allowlist needed.
 *
 * On click: open FeedbackModal. The page URL and page_context are captured
 * at open-time so they reflect the page the user was on when they clicked,
 * not whatever the URL becomes between click and submit.
 *
 * page_context shape per route (Q3A — pathname parser, no useParams needed
 * because AppLayout sits above <Routes>):
 *
 *   /                                   → { view: 'home' }
 *   /materials                          → { view: 'material_list' }
 *   /materials/:id                      → { view: 'material_detail', material_id }
 *   /suppliers                          → { view: 'supplier_list' }
 *   /suppliers/:name                    → { view: 'supplier_detail', supplier_name }
 *   /accounts                           → { view: 'account_list' }
 *   /accounts/:id                       → { view: 'account_detail', account_id }
 *   /contracts                          → { view: 'contracts' }
 *   /analytics                          → { view: 'analytics' }
 *   /rfp                                → { view: 'rfp_list' }
 *   /rfp/create/ai                      → { view: 'rfp_create' }
 *   /rfp/:id/bids                       → { view: 'rfp_bid_analysis', rfp_id }
 *   /rfp/:id/negotiation/:supplier      → { view: 'rfp_negotiation', rfp_id, supplier_name }
 *   /rfp/:id/executive-summary          → { view: 'rfp_executive_summary', rfp_id }
 *   /rfp/:id/snapshot                   → { view: 'rfp_snapshot', rfp_id }
 *   /rfp/:id                            → { view: 'rfp_detail', rfp_id }
 *   /profile                            → { view: 'profile' }
 *   /interview                          → { view: 'interview' }
 *   /captures                           → { view: 'captures' }
 *   (anything else)                     → { view: 'unknown' }
 *
 * Pattern order matters — more specific RFP sub-routes are matched before
 * the generic /rfp/:id catch-all.
 *
 * Lane 1382 (Ryan, 2026-10-04): on a page with a docked workspace chat (a sourcing
 * event, a Meeting Prep deck) the floating pill covered cards on first view, so those
 * pages put `FeedbackHeaderButton` in their own header instead.
 * Lane 1387 (Ryan, 2026-10-05): those two pages show no Feedback button at all for now.
 * They call useHideFeedback (workspace-chat/useHideFeedback.js), and workspaceChat.css
 * hides the floating pill (class `fb-float`) while they are on screen. Every other page
 * keeps the floating pill exactly as before.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { colors } from './AppLayout';
import FeedbackModal from './FeedbackModal';


function parsePageContext(pathname, search = '') {
  const ctx = {
    pathname,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };

  // Strip trailing slash for cleaner matching, but preserve "/" as itself.
  const path = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  if (path === '/' || path === '') {
    ctx.view = 'home';
    return ctx;
  }

  let m;

  // ---- RFP routes (specific before generic) ----
  if ((m = path.match(/^\/rfp\/create\/ai$/))) {
    ctx.view = 'rfp_create';
    return ctx;
  }
  if ((m = path.match(/^\/rfp\/([^/]+)\/bids$/))) {
    ctx.view = 'rfp_bid_analysis';
    ctx.rfp_id = decodeURIComponent(m[1]);
    return ctx;
  }
  if ((m = path.match(/^\/rfp\/([^/]+)\/negotiation\/([^/]+)$/))) {
    ctx.view = 'rfp_negotiation';
    ctx.rfp_id = decodeURIComponent(m[1]);
    ctx.supplier_name = decodeURIComponent(m[2]);
    return ctx;
  }
  if ((m = path.match(/^\/rfp\/([^/]+)\/executive-summary$/))) {
    ctx.view = 'rfp_executive_summary';
    ctx.rfp_id = decodeURIComponent(m[1]);
    return ctx;
  }
  if ((m = path.match(/^\/rfp\/([^/]+)\/snapshot$/))) {
    ctx.view = 'rfp_snapshot';
    ctx.rfp_id = decodeURIComponent(m[1]);
    return ctx;
  }
  if ((m = path.match(/^\/rfp\/([^/]+)$/))) {
    ctx.view = 'rfp_detail';
    ctx.rfp_id = decodeURIComponent(m[1]);
    return ctx;
  }
  if (path === '/rfp') {
    ctx.view = 'rfp_list';
    return ctx;
  }

  // ---- Materials ----
  if (path === '/materials') {
    ctx.view = 'material_list';
    return ctx;
  }
  if ((m = path.match(/^\/materials\/([^/]+)$/))) {
    ctx.view = 'material_detail';
    ctx.material_id = decodeURIComponent(m[1]);
    return ctx;
  }

  // ---- Suppliers ----
  if (path === '/suppliers') {
    ctx.view = 'supplier_list';
    return ctx;
  }
  if ((m = path.match(/^\/suppliers\/([^/]+)$/))) {
    ctx.view = 'supplier_detail';
    ctx.supplier_name = decodeURIComponent(m[1]);
    return ctx;
  }

  // ---- Customers / legacy accounts ----
  if (path === '/customers' || (path === '/accounts' && new URLSearchParams(search).has('customer'))) {
    const params = new URLSearchParams(search);
    const customer = params.get('customer'), account = params.get('account') || params.get('team');
    ctx.view = customer ? 'customer_detail' : account ? 'team_notes' : 'customer_list';
    if (customer) ctx.customer_code = customer;
    if (account) ctx.account_id = account;
    return ctx;
  }
  if (path === '/accounts') {
    ctx.view = 'account_list';
    return ctx;
  }
  if ((m = path.match(/^\/accounts\/([^/]+)$/))) {
    ctx.view = 'account_detail';
    ctx.account_id = decodeURIComponent(m[1]);
    return ctx;
  }

  // ---- Static single-segment routes ----
  if (path === '/contracts') { ctx.view = 'contracts'; return ctx; }
  if (path === '/analytics') { ctx.view = 'analytics'; return ctx; }
  if (path === '/profile') { ctx.view = 'profile'; return ctx; }
  if (path === '/interview') { ctx.view = 'interview'; return ctx; }
  if (path === '/captures') { ctx.view = 'captures'; return ctx; }

  ctx.view = 'unknown';
  return ctx;
}


// The page and its context are read when the buyer opens the form (see above). The
// form is portalled to <body>, so a header that holds the button can never clip it.
function useFeedbackForm() {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState({ pageUrl: '', pageContext: {} });
  const handleOpen = () => {
    setSnapshot({
      pageUrl: window.location.href,
      pageContext: parsePageContext(window.location.pathname, window.location.search),
    });
    setOpen(true);
  };
  const form = createPortal(
    <FeedbackModal
      open={open}
      onClose={() => setOpen(false)}
      pageUrl={snapshot.pageUrl}
      pageContext={snapshot.pageContext}
    />,
    document.body,
  );
  return { handleOpen, form };
}


// "Feedback" as a plain button in a page's own header; `className` gives it the
// page's own button style. Not on any page while Feedback is hidden on the workspace
// pages (Lane 1387); kept so it can come back without a rebuild.
export function FeedbackHeaderButton({ className }) {
  const { handleOpen, form } = useFeedbackForm();
  return (
    <>
      <button type="button" className={className} onClick={handleOpen} title="Send feedback">
        Feedback
      </button>
      {form}
    </>
  );
}


function FeedbackButton() {
  const { handleOpen, form } = useFeedbackForm();

  return (
    <>
      <button
        type="button"
        className="fb-float"
        onClick={handleOpen}
        aria-label="Send feedback"
        title="Send feedback"
        style={{
          position: 'fixed',
          right: '24px',
          bottom: '24px',
          zIndex: 99,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: colors.deepBlue,
          color: colors.white,
          border: 'none',
          borderRadius: '999px',
          padding: '10px 18px',
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          boxShadow: '0 6px 20px rgba(5, 28, 44, 0.35)',
          cursor: 'pointer',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 10px 28px rgba(5, 28, 44, 0.45)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(5, 28, 44, 0.35)';
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Feedback
      </button>

      {form}
    </>
  );
}

export default FeedbackButton;
